import { FaDesktop, FaRegMoon, FaRegSun } from 'react-icons/fa';
import { useLanguage } from '../../../../../../_CORE/Contexts/Translate/useLanguageContext';
import { useHeaderUtilities } from '../_Hooks/useHeaderUtilities';
import { UtilityDropdown, UtilityItem } from '../UtilityDropdown';
import { useTheme } from '../../../../../../_CORE/Contexts/Theme/ThemeAppContext';

/*
    🔥 ICONO POR TEMA — FLATICON UICONS

    ⚠️ NO SON EMOJI DEL SISTEMA.

       Un emoji lo dibuja el sistema operativo: en Windows sale distinto que en
       Mac, y en algunos entornos sale en blanco y negro o como un cuadrito.
       UIcons es una fuente propia, asi que se ve igual en todos lados y toma el
       color del texto como cualquier icono.

    ⚠️ LA FUENTE SE CARGA EN Utilities.css, ARRIBA DE TODO.

       Si no la cargas, estas clases no dibujan nada y queda un hueco. Por eso
       hay un respaldo mas abajo.

    ⚠️ NO REEMPLAZAN AL Icon DE THEME_META.

       El contexto ya trae FaSun, WiMoonAltFirstQuarter y FaDesktop. Quedan
       intactos: este mapa es la version del header y nada mas.

    ⚠️ PARA CAMBIAR UN ICONO, buscalo en flaticon.com/uicons y pegá su clase.
       El prefijo fi-rr- es "regular rounded"; hay fi-sr- (solid rounded),
       fi-br- (bold rounded) y fi-rs- (regular straight).
*/
const ThemeIcon = ({ code }) => {
    /*
        El header no debe depender de la fuente UIcons para que el selector de
        tema siga siendo visible. En algunos entornos la fuente tarda en cargar
        o no se resuelve y quedaba un botón vacío. React Icons ya forma parte
        del proyecto, por lo que funciona como icono principal estable.
    */
    if (code === 'light') return <FaRegSun size={18} aria-hidden="true" />;
    if (code === 'system') return <FaDesktop size={18} aria-hidden="true" />;
    return <FaRegMoon size={18} aria-hidden="true" />;
};

/*
    🔥 SELECTOR DE TEMA

    ⚠️ LA FUENTE ES ThemeAppContext, NO UN ESTADO PROPIO

       Antes este componente guardaba el tema por su cuenta con onApplyTheme y
       leia con onReadTheme, que apuntaban a OTRO almacenamiento. Resultado:
       elegias Claro, recargabas, y volvia Oscuro con el menu marcando Claro.

       Ahora la lista, el marcado y el guardado salen todos del contexto, que ya
       maneja light | dark | system y sigue al sistema operativo en vivo.

    ⚠️ LAS PROPS SIGUEN GANANDO

       Si alguien pasa theme / onChangeTheme desde afuera, mandan esos. El
       contexto queda de respaldo. Asi no se rompe ningun uso existente.
*/
export const ThemeApp = ({ theme = null, onChangeTheme = null }) => {

    const { useDataTheme, call_setTheme } = useTheme();

    const { useDataLanguage } = useLanguage();

    const TEXT = useDataLanguage.translations?.TEMPLATES?.header ?? {};

    const { onClose } = useHeaderUtilities();

    const THEMES = useDataTheme.availableThemesMeta ?? [];

    const THEME_LABEL = {
        light: TEXT.themeLight,
        dark: TEXT.themeDark,
        system: TEXT.themeSystem,
    };

    const CURRENT = theme ?? useDataTheme.currentTheme;

    const onSelect = (code) => {
        onClose();
        if (onChangeTheme) return onChangeTheme(code);
        call_setTheme(code);
    };

    /*
        ⚠️ EL DISPARADOR MUESTRA EL TEMA ELEGIDO, NO EL RESUELTO

           Antes mostraba el resuelto: con "Sistema" elegido y el SO en oscuro,
           veias una luna igual que si hubieras elegido Oscuro a mano. No habia
           forma de saber que estabas en automatico.

           Con el icono de la laptop eso queda claro de un vistazo, y el modo
           real igual se ve en toda la pantalla.

        ⚠️ Si el codigo activo no tiene icono, vuelve la luna. Un hueco donde
           deberia haber un icono se lee como un error de carga.
    */
    const ICON = <ThemeIcon code={CURRENT} />;

    return (
        <UtilityDropdown id="theme" label={TEXT.appearance} width={220} icon={ICON}>
            {
                THEMES.map((item) => (
                    <UtilityItem key={item.code} title={THEME_LABEL[item.code] ?? item.label}
                        icon={<ThemeIcon code={item.code} />}
                        isSelected={item.code === CURRENT}
                        onClick={() => onSelect(item.code)} />
                ))
            }
        </UtilityDropdown>
    );
};